'use client';

import { c as _c } from "react/compiler-runtime";
import './date-field.scss';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Locale, Matcher } from 'react-day-picker';
import { DateLib } from 'react-day-picker';
import { enUS, es, ko } from 'react-day-picker/locale';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LOCALES_CODES } from '@sola/contentful-model/lib/types.ts';
import { i18nConfig } from '@sola/schema/lib/i18n-config.ts';
import { toIsoDateString } from '@sola/utils/date.ts';
import { useDateMask } from '../../utils/masks.ts';
import { useDatePattern } from '../../utils/patterns.ts';
import { Calendar } from '../calendar/calendar.tsx';
import { FieldIconButton } from '../field/field.tsx';
import type { InputFieldProps } from '../input-field/input-field.tsx';
import { InputField } from '../input-field/input-field.tsx';
type DateFieldProps = InputFieldProps & {
  // The date to pre-select.
  defaultDate?: Date;
  // The number of days from the current day for which dates are allowed.
  minDaysOut?: number;
  // The maximum number of years shown in the calendar dropdown.
  maxYears?: number;
  shouldSetInitialDate?: boolean;
};

// TODO: Should we lazy load these react-day-picker locales? Maybe if we add more languages.
const localeMap = {
  [LOCALES_CODES.enum.en]: enUS,
  [LOCALES_CODES.enum.es]: es,
  [LOCALES_CODES.enum.ko]: ko
};
const formatDate = (date: Date | undefined, locale: Locale) => {
  if (!date) {
    return '';
  }
  const dateLib = new DateLib({
    locale
  });
  return dateLib.format(date, 'P');
};
const isValidDate = (date?: Date) => {
  if (!date) {
    return;
  }
  return !Number.isNaN(date.getTime());
};
const getDate = (val: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    return;
  }
  const date = new Date(val);
  if (isValidDate(date)) {
    return date;
  }
};
export const DateField: FC<DateFieldProps> = t0 => {
  const $ = _c(104);
  let defaultDate;
  let inputFieldProps;
  let minDaysOut;
  let name;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      name,
      minDaysOut,
      defaultDate,
      maxYears: t1,
      shouldSetInitialDate: t2,
      ...inputFieldProps
    } = t0);
    $[0] = t0;
    $[1] = defaultDate;
    $[2] = inputFieldProps;
    $[3] = minDaysOut;
    $[4] = name;
    $[5] = t1;
    $[6] = t2;
  } else {
    defaultDate = $[1];
    inputFieldProps = $[2];
    minDaysOut = $[3];
    name = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const maxYears = t1 === undefined ? 10 : t1;
  const shouldSetInitialDate = t2 === undefined ? true : t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["forms"];
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    i18n,
    t
  } = useTranslation(t3);
  const {
    trigger,
    register,
    setValue: setFormValue,
    formState
  } = useFormContext();
  const triggerRef = useRef(null);
  const hiddenFieldName = `${name}Date`;
  const currentLocaleString = i18n.language in localeMap ? i18n.language : i18nConfig.defaultLocale;
  const currentLocale = localeMap[currentLocaleString as keyof typeof localeMap];
  const [startDate, setStartDate] = useState();
  const [firstAllowedDate, setFirstAllowedDate] = useState();
  const [endDate, setEndDate] = useState();
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const [disableMatchers, setDisableMatchers] = useState(t4);
  const {
    mask,
    loadMask
  } = useDateMask(currentLocaleString);
  const {
    pattern,
    loadPattern
  } = useDatePattern(currentLocaleString);
  const [isOpen, setIsOpen] = useState(false);
  const [date, setDate] = useState();
  const [month, setMonth] = useState();
  const [value, setValue] = useState("");
  const initialized = useRef(false);
  let t5;
  if ($[9] !== currentLocale || $[10] !== defaultDate || $[11] !== maxYears || $[12] !== minDaysOut || $[13] !== shouldSetInitialDate) {
    t5 = () => {
      if (initialized.current) {
        return;
      }
      initialized.current = true;
      const now = new Date();
      const resolved = defaultDate ?? now;
      let first;
      if (minDaysOut) {
        first = new Date(now);
        first.setDate(first.getDate() + minDaysOut);
      }
      const start = first && first > resolved ? first : resolved;
      let end;
      if (maxYears) {
        end = new Date(start);
        end.setFullYear(end.getFullYear() + maxYears);
      }
      const matchers = [];
      if (first) {
        matchers.push({
          before: first
        });
      }
      setStartDate(start);
      setFirstAllowedDate(first);
      setEndDate(end);
      setDisableMatchers(matchers);
      setDate(start);
      setMonth(start);
      if (shouldSetInitialDate) {
        setValue(formatDate(start, currentLocale));
      }
    };
    $[9] = currentLocale;
    $[10] = defaultDate;
    $[11] = maxYears;
    $[12] = minDaysOut;
    $[13] = shouldSetInitialDate;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useEffect(t5, t6);
  let t7;
  if ($[16] !== formState || $[17] !== name || $[18] !== trigger) {
    t7 = () => {
      if (!name) {
        return;
      }
      if (!formState.touchedFields[name]) {
        return;
      }
      trigger(name);
    };
    $[16] = formState;
    $[17] = name;
    $[18] = trigger;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== name || $[21] !== value) {
    t8 = [value, name];
    $[20] = name;
    $[21] = value;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  useEffect(t7, t8);
  let t9;
  if ($[23] !== date || $[24] !== hiddenFieldName || $[25] !== setFormValue) {
    t9 = () => {
      setFormValue(hiddenFieldName, date?.toString());
    };
    $[23] = date;
    $[24] = hiddenFieldName;
    $[25] = setFormValue;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== date || $[28] !== setFormValue) {
    t10 = [date, setFormValue];
    $[27] = date;
    $[28] = setFormValue;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[30] !== isOpen) {
    t11 = () => {
      if (!isOpen) {
        triggerRef.current?.focus();
      }
    };
    t12 = [isOpen];
    $[30] = isOpen;
    $[31] = t11;
    $[32] = t12;
  } else {
    t11 = $[31];
    t12 = $[32];
  }
  useEffect(t11, t12);
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (newValue, newDate) => {
      if (newDate) {
        setDate(newDate);
      }
      setValue(newValue);
    };
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const handleValueChange = t13;
  let t14;
  if ($[34] !== hiddenFieldName || $[35] !== register) {
    t14 = register(hiddenFieldName);
    $[34] = hiddenFieldName;
    $[35] = register;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t14) {
    t15 = <input type="hidden" {...t14} />;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== currentLocale || $[40] !== date || $[41] !== firstAllowedDate || $[42] !== startDate || $[43] !== value) {
    t16 = e => {
      if ((e.relatedTarget as HTMLButtonElement)?.type === "button" && (!value || date && firstAllowedDate && date < firstAllowedDate)) {
        setMonth(startDate);
        handleValueChange(formatDate(startDate, currentLocale), startDate);
        setIsOpen(true);
      }
    };
    $[39] = currentLocale;
    $[40] = date;
    $[41] = firstAllowedDate;
    $[42] = startDate;
    $[43] = value;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <PopoverAnchor />;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t) {
    t18 = t("forms:date.selectDate");
    $[46] = t;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== currentLocale || $[49] !== isOpen || $[50] !== startDate || $[51] !== value) {
    t19 = () => {
      if (!value) {
        handleValueChange(formatDate(startDate, currentLocale), startDate);
      }
      if (!isOpen) {
        const el = triggerRef.current;
        if (el) {
          const rect = el.getBoundingClientRect();
          if (window.innerHeight - rect.bottom < 350) {
            window.scrollBy({
              top: 350,
              behavior: "smooth"
            });
          }
        }
      }
      setIsOpen(!isOpen);
    };
    $[48] = currentLocale;
    $[49] = isOpen;
    $[50] = startDate;
    $[51] = value;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t18 || $[54] !== t19) {
    t20 = <PopoverTrigger asChild={true}><FieldIconButton ref={triggerRef} iconName="calendar-date" label={t18} action={t19} /></PopoverTrigger>;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  const t21 = mask?.placeholder;
  let t22;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = e_0 => {
      handleValueChange(e_0.target.value, getDate(e_0.target.value));
    };
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== loadMask || $[58] !== loadPattern) {
    t23 = () => {
      loadMask();
      loadPattern();
    };
    $[57] = loadMask;
    $[58] = loadPattern;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== mask) {
    t24 = !!mask && {
      maskOptions: mask.options
    };
    $[60] = mask;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== pattern || $[63] !== t) {
    t25 = pattern ? {
      value: pattern,
      message: t("forms:errors.invalidDateFieldFormat")
    } : undefined;
    $[62] = pattern;
    $[63] = t;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== date || $[66] !== firstAllowedDate || $[67] !== minDaysOut || $[68] !== t) {
    t26 = () => {
      const isoDate = toIsoDateString(date);
      const isoFirstAllowedDate = toIsoDateString(firstAllowedDate);
      return !isoDate || !isoFirstAllowedDate || isoDate >= isoFirstAllowedDate ? true : t("forms:errors.firstAllowedDate", {
        minDaysOut
      });
    };
    $[65] = date;
    $[66] = firstAllowedDate;
    $[67] = minDaysOut;
    $[68] = t;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== date || $[71] !== t) {
    t27 = () => isValidDate(date) ? true : t("forms:errors.invalidDateFieldValue");
    $[70] = date;
    $[71] = t;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== t26 || $[74] !== t27) {
    t28 = {
      minDaysOut: t26,
      validDate: t27
    };
    $[73] = t26;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  let t29;
  if ($[76] !== inputFieldProps || $[77] !== name || $[78] !== t16 || $[79] !== t20 || $[80] !== t21 || $[81] !== t23 || $[82] !== t24 || $[83] !== t25 || $[84] !== t28 || $[85] !== value) {
    t29 = <InputField onBlur={t16} name={name} value={value} type="text" inputMode="numeric" footer={t17} iconElement={t20} placeholder={t21} onChange={t22} onVisible={t23} {...t24} pattern={t25} validate={t28} {...inputFieldProps} />;
    $[76] = inputFieldProps;
    $[77] = name;
    $[78] = t16;
    $[79] = t20;
    $[80] = t21;
    $[81] = t23;
    $[82] = t24;
    $[83] = t25;
    $[84] = t28;
    $[85] = value;
    $[86] = t29;
  } else {
    t29 = $[86];
  }
  let t30;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      position: "fixed",
      willChange: "transform",
      right: "0px"
    };
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  const t31 = date ?? startDate;
  let t32;
  if ($[88] !== currentLocale || $[89] !== startDate) {
    t32 = selectedDate => {
      const calendarDate = selectedDate || startDate;
      handleValueChange(formatDate(calendarDate, currentLocale), calendarDate);
      setIsOpen(false);
    };
    $[88] = currentLocale;
    $[89] = startDate;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] !== currentLocale || $[92] !== disableMatchers || $[93] !== endDate || $[94] !== firstAllowedDate || $[95] !== month || $[96] !== t31 || $[97] !== t32) {
    t33 = <PopoverContent className="DateField__content" align="end" sideOffset={2} avoidCollisions={false} style={t30}><Calendar className="DateField__calendar" mode="single" locale={currentLocale} selected={t31} disabled={disableMatchers} month={month} startMonth={firstAllowedDate} endMonth={endDate} onMonthChange={setMonth} onSelect={t32} autoFocus={true} /></PopoverContent>;
    $[91] = currentLocale;
    $[92] = disableMatchers;
    $[93] = endDate;
    $[94] = firstAllowedDate;
    $[95] = month;
    $[96] = t31;
    $[97] = t32;
    $[98] = t33;
  } else {
    t33 = $[98];
  }
  let t34;
  if ($[99] !== isOpen || $[100] !== t15 || $[101] !== t29 || $[102] !== t33) {
    t34 = <div className="DateField"><Popover open={isOpen} onOpenChange={setIsOpen}>{t15}{t29}{t33}</Popover></div>;
    $[99] = isOpen;
    $[100] = t15;
    $[101] = t29;
    $[102] = t33;
    $[103] = t34;
  } else {
    t34 = $[103];
  }
  return t34;
};